import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Building,
  Map,
  Globe,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  FileText,
  X,
  Filter
} from 'lucide-react';
import { locationService } from '../../services/locationService';

const Location = () => {
  const [activeTab, setActiveTab] = useState('state');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state for states
  const [statePagination, setStatePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Pagination state for districts
  const [districtPagination, setDistrictPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Pagination state for blocks
  const [blockPagination, setBlockPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Pagination state for centres
  const [centrePagination, setCentrePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Debounced search for districts
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // States data loaded from API
  const [states, setStates] = useState([]);
  // All states for modal forms (unpaginated)
  const [allStates, setAllStates] = useState([]);

  // Cascading dropdown data for Centre form
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);

  // Centre code split inputs state
  const [centreCodePrefix, setCentreCodePrefix] = useState(''); // Non-editable block_id part
  const [centreCodeSuffix, setCentreCodeSuffix] = useState(''); // Editable custom part

  const [districts, setDistricts] = useState([
    { id: 1, name: 'Coimbatore', code: 'COI', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 2, name: 'Salem', code: 'SAL', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 3, name: 'Erode', code: 'ERO', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 4, name: 'Bangalore Urban', code: 'BAN', state_id: 2, state_code: 'KA', state_name: 'Karnataka', status: 'active' },
    { id: 5, name: 'Mysore', code: 'MYS', state_id: 2, state_code: 'KA', state_name: 'Karnataka', status: 'active' },
    { id: 6, name: 'Thrissur', code: 'THR', state_id: 3, state_code: 'KL', state_name: 'Kerala', status: 'active' }
  ]);

  const [blocks, setBlocks] = useState([
    { id: 1, name: 'Coimbatore North', code: 'TNCOI01', district_id: 1, district_code: 'TNCOI', district_name: 'Coimbatore', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 2, name: 'Coimbatore South', code: 'TNCOI02', district_id: 1, district_code: 'TNCOI', district_name: 'Coimbatore', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 3, name: 'Salem East', code: 'TNSAL01', district_id: 2, district_code: 'TNSAL', district_name: 'Salem', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 4, name: 'Salem West', code: 'TNSAL02', district_id: 2, district_code: 'TNSAL', district_name: 'Salem', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 5, name: 'Erode Central', code: 'TNERO01', district_id: 3, district_code: 'TNERO', district_name: 'Erode', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 6, name: 'Bangalore North', code: 'KABAN01', district_id: 4, district_code: 'KABAN', district_name: 'Bangalore Urban', state_id: 2, state_code: 'KA', state_name: 'Karnataka', status: 'active' }
  ]);

  const [centres, setCentres] = useState([
    { id: 1, name: 'TNAU Main Campus', code: 'TNCOICNTMC', type: 'TNAU', block_id: 1, block_code: 'TNCOI01', block_name: 'Coimbatore North', district_id: 1, district_code: 'TNCOI', district_name: 'Coimbatore', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 2, name: 'Agricultural Research Station', code: 'TNCOICNARS', type: 'TNAU', block_id: 1, block_code: 'TNCOI01', block_name: 'Coimbatore North', district_id: 1, district_code: 'TNCOI', district_name: 'Coimbatore', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 3, name: 'Extension Centre', code: 'TNCOICSEXT', type: 'GoTN', block_id: 2, block_code: 'TNCOI02', block_name: 'Coimbatore South', district_id: 1, district_code: 'TNCOI', district_name: 'Coimbatore', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 4, name: 'Salem Research Centre', code: 'TNSALSESRC', type: 'TNAU', block_id: 3, block_code: 'TNSAL01', block_name: 'Salem East', district_id: 2, district_code: 'TNSAL', district_name: 'Salem', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' },
    { id: 5, name: 'Erode Field Station', code: 'TNEROECEFS', type: 'GoTN', block_id: 5, block_code: 'TNERO01', block_name: 'Erode Central', district_id: 3, district_code: 'TNERO', district_name: 'Erode', state_id: 1, state_code: 'TN', state_name: 'Tamil Nadu', status: 'active' }
  ]);

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();

  // Watch form fields for cascading dropdown behavior
  const watchedStateCode = watch('stateCode');
  const watchedDistrictCode = watch('districtCode');
  const watchedBlockCode = watch('blockCode');

  // Handle state change - reset district and block dropdowns
  useEffect(() => {
    if (activeTab === 'centre' && watchedStateCode) {
      // Load districts for selected state
      loadDistrictsByState(watchedStateCode);
      // Reset district and block selections
      setValue('districtCode', '');
      setValue('blockCode', '');
      setAvailableDistricts([]);
      setAvailableBlocks([]);
      setCentreCodePrefix('');
      setCentreCodeSuffix('');
    }
  }, [watchedStateCode, activeTab, setValue]);

  // Handle district change - reset block dropdown
  useEffect(() => {
    if (activeTab === 'centre' && watchedDistrictCode) {
      // Load blocks for selected district
      loadBlocksByDistrict(watchedDistrictCode);
      // Reset block selection
      setValue('blockCode', '');
      setAvailableBlocks([]);
      setCentreCodePrefix('');
      setCentreCodeSuffix('');
    }
  }, [watchedDistrictCode, activeTab, setValue]);

  // Update centre code prefix when block is selected
  useEffect(() => {
    if (activeTab === 'centre' && watchedBlockCode) {
      const selectedBlock = availableBlocks.find(block => block.block_id == watchedBlockCode);
      if (selectedBlock) {
        setCentreCodePrefix(selectedBlock.block_id || '');
      }
    }
  }, [watchedBlockCode, activeTab, availableBlocks]);

  const tabs = [
    { id: 'state', name: 'State', icon: Globe, data: states },
    { id: 'district', name: 'District', icon: Map, data: districts },
    { id: 'block', name: 'Block', icon: Building, data: blocks },
    { id: 'centre', name: 'Centre', icon: MapPin, data: centres }
  ];

  const currentData = tabs.find(tab => tab.id === activeTab)?.data || [];

  const [statusFilter, setStatusFilter] = React.useState('all');

  // Normalize various status shapes to 'active' | 'inactive'
  const normalizeStatus = (raw) => {
    if (raw === undefined || raw === null) return 'inactive';
    const v = String(raw).toLowerCase();
    if (v === '1' || v === 'true' || v === 'active' || v === 'enabled') return 'active';
    if (v === '0' || v === 'false' || v === 'inactive' || v === 'disabled') return 'inactive';
    return v.includes('active') ? 'active' : 'inactive';
  };

  // API Integration Functions
  const loadAllStates = async () => {
    try {
      const response = await locationService.getStates({ limit: 1000 }); // Load all states
      if (response.success && response.data) {
        setAllStates(response.data);
      } else {
        setAllStates(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load all states for modal forms:', error.message);
    }
  };

  const loadStates = async (page = 1, search = '', status = statusFilter) => {
    try {
      setIsLoading(true);
      const params = {
        page: page,
        limit: statePagination.limit
      };
      
      // Add search parameter if provided
      if (search) {
        params.search = search;
      }
      // Add status filter if not 'all'
      if (status && status !== 'all') {
        params.status = status;
      }
      
      const response = await locationService.getStates(params);
      
      if (response.success && response.data) {
        setStates(response.data);
        // Update pagination if available
        if (response.pagination) {
          setStatePagination(response.pagination);
        }
      } else {
        setStates(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load states from API:', error.message);
      // Keep existing data if API fails
    } finally {
      setIsLoading(false);
    }
  };

  const loadDistricts = async (page = 1, search = '', status = statusFilter) => {
    try {
      setIsLoading(true);
      const params = {
        page: page,
        limit: districtPagination.limit
      };
      
      // Add search parameter if provided
      if (search) {
        params.search = search;
      }
      // Add status filter if not 'all'
      if (status && status !== 'all') {
        params.status = status;
      }
      
      const response = await locationService.getDistricts(params);
      
      if (response.success && response.data) {
        setDistricts(response.data);
        // Update pagination if available
        if (response.pagination) {
          setDistrictPagination(response.pagination);
        }
      } else {
        setDistricts(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load districts from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    } finally {
      setIsLoading(false);
    }
  };

  const loadBlocks = async (page = 1, search = '', status = statusFilter) => {
    try {
      setIsLoading(true);
      const params = {
        page: page,
        limit: blockPagination.limit
      };
      if (search) {
        params.search = search;
      }
      // Add status filter if not 'all'
      if (status && status !== 'all') {
        params.status = status;
      }
      const response = await locationService.getBlocks(params);
      if (response.success && response.data) {
        setBlocks(response.data);
        if (response.pagination) {
          setBlockPagination(response.pagination);
        }
      } else {
        setBlocks(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load blocks from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    } finally {
      setIsLoading(false);
    }
  };

  const loadCentres = async (page = 1, search = '', status = statusFilter) => {
    try {
      setIsLoading(true);
      const params = {
        page: page,
        limit: centrePagination.limit
      };
      
      // Add search parameter if provided
      if (search) {
        params.search = search;
      }
      // Add status filter if not 'all'
      if (status && status !== 'all') {
        params.status = status;
      }
      
      const response = await locationService.getCentres(params);
      if (response.success && response.data) {
        setCentres(response.data);
        if (response.pagination) {
          setCentrePagination(response.pagination);
        }
      } else {
        setCentres(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load centres from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    } finally {
      setIsLoading(false);
    }
  };

  // Load districts by state for cascading dropdown
  const loadDistrictsByState = async (stateId) => {
    try {
      if (!stateId) {
        setAvailableDistricts([]);
        return;
      }
      
      const response = await locationService.getDistrictsByState(stateId);
      if (response.success && response.data) {
        setAvailableDistricts(response.data);
      } else {
        setAvailableDistricts(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load districts by state:', error.message);
      setAvailableDistricts([]);
    }
  };

  // Load blocks by district for cascading dropdown
  const loadBlocksByDistrict = async (districtId) => {
    try {
      if (!districtId) {
        setAvailableBlocks([]);
        return;
      }
      
      const response = await locationService.getBlocksByDistrict(districtId);
      if (response.success && response.data) {
        setAvailableBlocks(response.data);
      } else {
        setAvailableBlocks(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to load blocks by district:', error.message);
      setAvailableBlocks([]);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAllStates(); // Load all states for modal forms
    loadStates(statePagination.page, '', statusFilter);
    loadDistricts(districtPagination.page, '', statusFilter);
    loadBlocks(blockPagination.page, '', statusFilter);
    loadCentres(centrePagination.page, '', statusFilter);
  }, []);

  // Debounced search effect for districts
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reload lists when status filter or active tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'state':
        setStatePagination(prev => ({ ...prev, page: 1 }));
        loadStates(1, debouncedSearchTerm, statusFilter);
        break;
      case 'district':
        setDistrictPagination(prev => ({ ...prev, page: 1 }));
        loadDistricts(1, debouncedSearchTerm, statusFilter);
        break;
      case 'block':
        setBlockPagination(prev => ({ ...prev, page: 1 }));
        loadBlocks(1, debouncedSearchTerm, statusFilter);
        break;
      case 'centre':
        setCentrePagination(prev => ({ ...prev, page: 1 }));
        loadCentres(1, debouncedSearchTerm, statusFilter);
        break;
    }
  }, [statusFilter, activeTab]);

  // Handle debounced search for districts
  useEffect(() => {
    if (activeTab === 'district') {
      if (debouncedSearchTerm !== '') {
        setDistrictPagination(prev => ({ ...prev, page: 1 }));
        loadDistricts(1, debouncedSearchTerm, statusFilter);
      } else if (debouncedSearchTerm === '') {
        setDistrictPagination(prev => ({ ...prev, page: 1 }));
        loadDistricts(1, '', statusFilter);
      }
    }
  }, [debouncedSearchTerm, activeTab]);

  // Handle debounced search for blocks
  useEffect(() => {
    if (activeTab === 'block') {
      if (debouncedSearchTerm !== '') {
        setBlockPagination(prev => ({ ...prev, page: 1 }));
        loadBlocks(1, debouncedSearchTerm, statusFilter);
      } else if (debouncedSearchTerm === '') {
        setBlockPagination(prev => ({ ...prev, page: 1 }));
        loadBlocks(1, '', statusFilter);
      }
    }
  }, [debouncedSearchTerm, activeTab]);

  // Handle debounced search for states
  useEffect(() => {
    if (activeTab === 'state') {
      if (debouncedSearchTerm !== '') {
        setStatePagination(prev => ({ ...prev, page: 1 }));
        loadStates(1, debouncedSearchTerm, statusFilter);
      } else if (debouncedSearchTerm === '') {
        setStatePagination(prev => ({ ...prev, page: 1 }));
        loadStates(1, '', statusFilter);
      }
    }
  }, [debouncedSearchTerm, activeTab]);

  // Handle debounced search for centres
  useEffect(() => {
    if (activeTab === 'centre') {
      if (debouncedSearchTerm !== '') {
        setCentrePagination(prev => ({ ...prev, page: 1 }));
        loadCentres(1, debouncedSearchTerm, statusFilter);
      } else if (debouncedSearchTerm === '') {
        setCentrePagination(prev => ({ ...prev, page: 1 }));
        loadCentres(1, '', statusFilter);
      }
    }
  }, [debouncedSearchTerm, activeTab]);

  const filteredData = currentData.filter((item) => {
    const q = (searchTerm || '').toLowerCase();
    if (!q) return true;
    
    // For states, districts, blocks, and centres, don't apply local search since we're using API search with pagination
    if (activeTab === 'state' || activeTab === 'district' || activeTab === 'block' || activeTab === 'centre') {
        return true;
    }
    
    // All location types now use API search with pagination
    return true;
  });

  // Helper to generate numeric 2-digit code for blocks within a district
  const getBlockNumericCode = (item) => {
    const sameDistrictBlocks = blocks
      .filter(b => b.districtCode === item.districtCode)
      .sort((a, b) => (a.id || 0) - (b.id || 0));
    const index = sameDistrictBlocks.findIndex(b => b.id === item.id);
    const order = index >= 0 ? index + 1 : sameDistrictBlocks.length + 1;
    return order.toString().padStart(2, '0');
  };

  // Generate hierarchical codes
  const generateCode = (item, level) => {
        return item.code;
    // switch (level) {
    //   case 'state':
    //     return item.code;
    //   case 'district':
    //     return `${item.stateCode}${item.code}`;
    //   case 'block':
    //     // Block code should end with a 2-digit number within the district
    //     return `${item.stateCode}${item.districtCode}${getBlockNumericCode(item)}`;
    //   case 'centre':
    //     return `${item.stateCode}${item.districtCode}${item.blockCode}${item.code}`;
    //   default:
    //     return item.code;
    // }
  };

  // CSV Export Functions
  const exportToCSV = () => {
    // CSV export remains local as API doesn't provide CSV format
    const data = filteredData.map(item => {
      const row = {};
      switch (activeTab) {
        case 'state':
          row.CODE = item.state_id;
          row.NAME = item.name;
          row.STATUS = item.status;
          break;
        case 'district':
          const stateName = (states.find(s => s.code === item.state_code) || {}).name || '';
          row.CODE = item.district_id;
          row.NAME = item.name;
          row.STATE = stateName;
          row.STATUS = item.status;
          break;
        case 'block':
          const stateNameBlock = (states.find(s => s.code === item.stateCode) || {}).name || '';
          const districtNameBlock = (districts.find(d => d.code === item.districtCode) || {}).name || '';
          row.CODE = item.block_id;
          row.NAME = item.name;
          row.DISTRICT = districtNameBlock;
          row.STATE = stateNameBlock;
          row.STATUS = item.status;
          break;
        case 'centre':
          row.CODE = generateCode(item, 'centre');
          row.NAME = item.name;
          row.TYPE = item.type;
          row.BLOCK = item.block_name;
          row.DISTRICT = item.district_name;
          row.STATE = item.state_name;
          row.STATUS = item.status;
          break;
      }
      return row;
    });

    const headers = Object.keys(data[0] || {}).join(',');
    const csvContent = `${headers}\n${data.map(row => Object.values(row).map(val => `"${val}"`).join(',')).join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_data.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} data exported as CSV!`);
  };

  const exportToExcel = async () => {
    try {
      // Try to use API export first
      let blob;
      switch (activeTab) {
        case 'state':
          blob = await locationService.exportStates();
          break;
        case 'district':
          blob = await locationService.exportDistricts();
          break;
        case 'block':
          blob = await locationService.exportBlocks();
          break;
        case 'centre':
          blob = await locationService.exportCentres();
          break;
        default:
          throw new Error('Unsupported export type');
      }
      
      // Create download link for API export
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_data.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} data exported as Excel!`);
    } catch (error) {
      console.warn('API export failed, falling back to local export:', error.message);
      // Fallback to local export
    const data = filteredData.map(item => {
      const row = {};
      switch (activeTab) {
        case 'state':
          row.CODE = item.state_id;
          row.NAME = item.name;
          row.STATUS = item.status;
          break;
        case 'district':
            const stateName = (states.find(s => s.code === item.state_code) || {}).name || '';
          row.CODE = item.district_id;
          row.NAME = item.name;
          row.STATE = stateName;
          row.STATUS = item.status;
          break;
        case 'block':
          row.CODE = item.block_id;
          row.NAME = item.name;
          row.DISTRICT = item.district_name;
          row.STATE = item.state_name;
          row.STATUS = item.status;
          break;
        case 'centre':
          const stateNameCentre = (states.find(s => s.code === item.stateCode) || {}).name || '';
          const districtNameCentre = (districts.find(d => d.code === item.districtCode) || {}).name || '';
          const blockNameCentre = (blocks.find(b => b.code === item.blockCode) || {}).name || '';
          row.CODE = item.centre_id;
          row.NAME = item.name;
          row.TYPE = item.type;
          row.BLOCK = blockNameCentre;
          row.DISTRICT = districtNameCentre;
          row.STATE = stateNameCentre;
          row.STATUS = item.status;
          break;
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${activeTab}_data.xlsx`);
    toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} data exported as Excel!`);
    }
  };

  const downloadTemplate = () => {
    const templates = {
      state: 'name,code\nTamil Nadu,TN\nKarnataka,KA',
      district: 'name,code,stateCode\nCoimbatore,COI,TN\nSalem,SAL,TN',
      block: 'name,code,districtCode,stateCode\nCoimbatore North,CN,COI,TN\nCoimbatore South,CS,COI,TN'
    };

    const template = templates[activeTab];
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'state':
          // Send state_id directly since form now uses state_id field
          response = await locationService.createState(data);
          if (response.success) {
            await loadStates(statePagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('State added successfully!');
          } else {
            throw new Error(response.message || 'Failed to add state');
          }
          break;
        case 'district':
          // Map stateCode to state_id for API
          const districtData = {
            ...data,
            state_id: data.stateCode
          };
          // Remove stateCode from the data sent to API
          delete districtData.stateCode;
          console.log('Original district data:', data);
          console.log('Transformed district data for API:', districtData);
          response = await locationService.createDistrict(districtData);
          if (response.success) {
            await loadDistricts(districtPagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('District added successfully!');
          } else {
            throw new Error(response.message || 'Failed to add district');
          }
          break;
        case 'block':
          // Map stateCode to state_id and districtCode to district_id for API
          const blockData = {
            ...data,
            state_id: data.stateCode,
            district_id: data.districtCode
          };
          // Remove stateCode and districtCode from the data sent to API
          delete blockData.stateCode;
          delete blockData.districtCode;
          console.log('Original block data:', data);
          console.log('Transformed block data for API:', blockData);
          response = await locationService.createBlock(blockData);
          if (response.success) {
            await loadBlocks(blockPagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('Block added successfully!');
          } else {
            throw new Error(response.message || 'Failed to add block');
          }
          break;
        case 'centre':
          // Join centre code parts with '-'
          const fullCentreCode = `${centreCodePrefix}-${centreCodeSuffix}`;
          
          // Map stateCode to state_id, districtCode to district_id, and blockCode to block_id for API
          const centreData = {
            ...data,
            centre_id: fullCentreCode, // Use joined centre code
            state_id: data.stateCode,
            district_id: data.districtCode,
            block_id: data.blockCode
          };
          // Remove stateCode, districtCode, and blockCode from the data sent to API
          delete centreData.stateCode;
          delete centreData.districtCode;
          delete centreData.blockCode;
          console.log('Original centre data:', data);
          console.log('Centre code parts:', { prefix: centreCodePrefix, suffix: centreCodeSuffix, full: fullCentreCode });
          console.log('Transformed centre data for API:', centreData);
          response = await locationService.createCentre(centreData);
          if (response.success) {
            await loadCentres(centrePagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('Centre added successfully!');
          } else {
            throw new Error(response.message || 'Failed to add centre');
          }
          break;
      }
      setShowAddModal(false);
      reset();
      // Reset centre code state
      setCentreCodePrefix('');
      setCentreCodeSuffix('');
      // Reset cascading dropdown data
      setAvailableDistricts([]);
      setAvailableBlocks([]);
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to add item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'state':
          // Send state_id directly since form now uses state_id field
          response = await locationService.updateState(editingItem.id, data);
          if (response.success) {
            await loadStates(statePagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('State updated successfully!');
          } else {
            throw new Error(response.message || 'Failed to update state');
          }
          break;
        case 'district':
          // Map stateCode to state_id for API
          const editDistrictData = {
            ...data,
            state_id: data.stateCode
          };
          // Remove stateCode from the data sent to API
          delete editDistrictData.stateCode;
          console.log('Original edit district data:', data);
          console.log('Transformed edit district data for API:', editDistrictData);
          response = await locationService.updateDistrict(editingItem.id, editDistrictData);
          if (response.success) {
            await loadDistricts(districtPagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('District updated successfully!');
          } else {
            throw new Error(response.message || 'Failed to update district');
          }
          break;
        case 'block':
          // Map stateCode to state_id and districtCode to district_id for API
          const editBlockData = {
            ...data,
            state_id: data.stateCode,
            district_id: data.districtCode
          };
          // Remove stateCode and districtCode from the data sent to API
          delete editBlockData.stateCode;
          delete editBlockData.districtCode;
          console.log('Original edit block data:', data);
          console.log('Transformed edit block data for API:', editBlockData);
          response = await locationService.updateBlock(editingItem.id, editBlockData);
          if (response.success) {
            await loadBlocks(blockPagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('Block updated successfully!');
          } else {
            throw new Error(response.message || 'Failed to update block');
          }
          break;
        case 'centre':
          // Join centre code parts with '-'
          const editFullCentreCode = `${centreCodePrefix}-${centreCodeSuffix}`;
          
          // Map stateCode to state_id, districtCode to district_id, and blockCode to block_id for API
          const editCentreData = {
            ...data,
            centre_id: editFullCentreCode, // Use joined centre code
            state_id: data.stateCode,
            district_id: data.districtCode,
            block_id: data.blockCode
          };
          // Remove stateCode, districtCode, and blockCode from the data sent to API
          delete editCentreData.stateCode;
          delete editCentreData.districtCode;
          delete editCentreData.blockCode;
          console.log('Original edit centre data:', data);
          console.log('Edit centre code parts:', { prefix: centreCodePrefix, suffix: centreCodeSuffix, full: editFullCentreCode });
          console.log('Transformed edit centre data for API:', editCentreData);
          response = await locationService.updateCentre(editingItem.id, editCentreData);
          if (response.success) {
            await loadCentres(centrePagination.page, debouncedSearchTerm, statusFilter); // Reload respecting filters
            toast.success('Centre updated successfully!');
          } else {
            throw new Error(response.message || 'Failed to update centre');
          }
          break;
      }
      setShowAddModal(false);
      setEditingItem(null);
      reset();
      // Reset centre code state
      setCentreCodePrefix('');
      setCentreCodeSuffix('');
      // Reset cascading dropdown data
      setAvailableDistricts([]);
      setAvailableBlocks([]);
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to update item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    // Get item name for better confirmation message
    let itemName = '';
    let itemType = '';
    
    switch (activeTab) {
      case 'state':
        const state = states.find(s => s.id === itemId);
        itemName = state?.name || 'Unknown State';
        itemType = 'State';
        break;
      case 'district':
        const district = districts.find(d => d.id === itemId);
        itemName = district?.name || 'Unknown District';
        itemType = 'District';
        break;
      case 'block':
        const block = blocks.find(b => b.id === itemId);
        itemName = block?.name || 'Unknown Block';
        itemType = 'Block';
        break;
      case 'centre':
        const centre = centres.find(c => c.id === itemId);
        itemName = centre?.name || 'Unknown Centre';
        itemType = 'Centre';
        break;
    }

    const confirmMessage = `Are you sure you want to delete the ${itemType.toLowerCase()} "${itemName}"? This action will permanently remove it from the database and cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    try {
      let response;
      console.log(`Deleting ${itemType.toLowerCase()} with ID:`, itemId);
      
      switch (activeTab) {
        case 'state':
          response = await locationService.deleteState(itemId);
          if (response.success) {
            await loadStates(); // Reload data from API
            toast.success(`${itemType} "${itemName}" deleted successfully from database!`);
          } else {
            throw new Error(response.message || 'Failed to delete state');
          }
          break;
        case 'district':
          response = await locationService.deleteDistrict(itemId);
          if (response.success) {
            await loadDistricts(); // Reload data from API
            toast.success(`${itemType} "${itemName}" deleted successfully from database!`);
          } else {
            throw new Error(response.message || 'Failed to delete district');
          }
          break;
        case 'block':
          response = await locationService.deleteBlock(itemId);
          if (response.success) {
            await loadBlocks(); // Reload data from API
            toast.success(`${itemType} "${itemName}" deleted successfully from database!`);
          } else {
            throw new Error(response.message || 'Failed to delete block');
          }
          break;
        case 'centre':
          response = await locationService.deleteCentre(itemId);
          if (response.success) {
            await loadCentres(); // Reload data from API
            toast.success(`${itemType} "${itemName}" deleted successfully from database!`);
          } else {
            throw new Error(response.message || 'Failed to delete centre');
          }
          break;
      }
      console.log(`${itemType} deletion successful:`, response);
    } catch (error) {
      console.error(`${itemType} deletion failed:`, error);
      toast.error(`Failed to delete ${itemType.toLowerCase()}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setValue('name', item.name);
    setValue('code', item.code);
    setValue('status', item.status);
    if (activeTab === 'state') {
      setValue('state_id', item.state_id);
    }
    if (activeTab === 'district') {
      setValue('stateCode', item.state_id);
      setValue('district_id', item.district_id);
    }
    if (activeTab === 'block') {
      setValue('districtCode', item.district_id);
      setValue('stateCode', item.state_id);
      setValue('block_id', item.block_id);
    }
    if (activeTab === 'centre') {
      setValue('stateCode', item.state_id);
      setValue('type', item.type);
      
      // Load cascading data for editing
      if (item.state_id) {
        loadDistrictsByState(item.state_id).then(() => {
          setValue('districtCode', item.district_id);
          if (item.district_id) {
            loadBlocksByDistrict(item.district_id).then(() => {
              setValue('blockCode', item.block_id);
              // Split centre code for editing
              if (item.centre_id && item.centre_id.includes('-')) {
                const [prefix, suffix] = item.centre_id.split('-');
                setCentreCodePrefix(prefix || '');
                setCentreCodeSuffix(suffix || '');
              } else {
                // Fallback if no '-' found
                setCentreCodePrefix(item.block_id || '');
                setCentreCodeSuffix(item.centre_id || '');
              }
            });
          }
        });
      }
    }
    setShowAddModal(true);
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'state':
          return (
            <>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State CODE</label>
              <input
                type="text"
                {...register('state_id', { required: 'State CODE is required' })}
                  className="input-field"
                placeholder="e.g., TN, KA, AP"
                maxLength={10}
              />
              {errors.state_id && <p className="text-red-500 text-sm mt-1">{errors.state_id.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State Name</label>
                <input
                  type="text"
                  {...register('name', { required: 'State name is required' })}
                  className="input-field"
                  placeholder="e.g., Tamil Nadu"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  defaultValue="active"
                  {...register('status', { required: 'Status is required' })}
                  className="input-field"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
              </div>
            </>
          );
      case 'district':
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  {...register('stateCode', { required: 'State is required' })}
                  className="input-field"
                >
                  <option value="">Select State</option>
                {allStates.map(state => (
                  <option key={state.id} value={state.state_id}>{state.name}</option>
                  ))}
                </select>
                {errors.stateCode && <p className="text-red-500 text-sm mt-1">{errors.stateCode.message}</p>}
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">District CODE</label>
              <input
                type="text"
                {...register('district_id', { required: 'District CODE is required' })}
                  className="input-field"
                placeholder="e.g., TNCOI, TNSAL, TNERO"
                maxLength={10}
              />
              {errors.district_id && <p className="text-red-500 text-sm mt-1">{errors.district_id.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                <input
                  type="text"
                  {...register('name', { required: 'District name is required' })}
                  className="input-field"
                  placeholder="e.g., Coimbatore"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  defaultValue="active"
                  {...register('status', { required: 'Status is required' })}
                  className="input-field"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
              </div>
            </>
          );
      case 'block':
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  {...register('stateCode', { required: 'State is required' })}
                  className="input-field"
                >
                  <option value="">Select State</option>
                {allStates.map(state => (
                  <option key={state.id} value={state.state_id}>{state.name}</option>
                  ))}
                </select>
                {errors.stateCode && <p className="text-red-500 text-sm mt-1">{errors.stateCode.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <select
                  {...register('districtCode', { required: 'District is required' })}
                  className="input-field"
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                  <option key={district.id} value={district.district_id}>{district.name}</option>
                  ))}
                </select>
                {errors.districtCode && <p className="text-red-500 text-sm mt-1">{errors.districtCode.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Block Code</label>
              <input
                type="text"
                {...register('block_id', { required: 'Block Code is required' })}
                  className="input-field"
                placeholder="e.g., TNCOI01, TNSAL01, TNERO01"
                maxLength={10}
              />
              {errors.block_id && <p className="text-red-500 text-sm mt-1">{errors.block_id.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Block Name</label>
                <input
                  type="text"
                  {...register('name', { required: 'Block name is required' })}
                  className="input-field"
                  placeholder="e.g., Coimbatore North"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  defaultValue="active"
                  {...register('status', { required: 'Status is required' })}
                  className="input-field"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
              </div>
            </>
          );
      case 'centre':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                {...register('stateCode', { required: 'State is required' })}
                className="input-field"
              >
                <option value="">Select State</option>
                {allStates.map(state => (
                  <option key={state.id} value={state.state_id}>{state.name}</option>
                ))}
              </select>
              {errors.stateCode && <p className="text-red-500 text-sm mt-1">{errors.stateCode.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
              <select
                {...register('districtCode', { required: 'District is required' })}
                className="input-field"
                disabled={!watchedStateCode}
              >
                <option value="">{watchedStateCode ? 'Select District' : 'Select State first'}</option>
                {availableDistricts.map(district => (
                  <option key={district.id} value={district.district_id}>{district.name}</option>
                ))}
              </select>
              {errors.districtCode && <p className="text-red-500 text-sm mt-1">{errors.districtCode.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Block</label>
              <select
                {...register('blockCode', { required: 'Block is required' })}
                className="input-field"
                disabled={!watchedDistrictCode}
              >
                <option value="">{watchedDistrictCode ? 'Select Block' : 'Select District first'}</option>
                {availableBlocks.map(block => (
                  <option key={block.id} value={block.block_id}>{block.name}</option>
                ))}
              </select>
              {errors.blockCode && <p className="text-red-500 text-sm mt-1">{errors.blockCode.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
              <select
                {...register('type', { required: 'Centre is required' })}
                className="input-field"
              >
                <option value="">Select Centre</option>
                <option value="TNAU">TNAU</option>
                <option value="GoTN">GoTN</option>
              </select>
              {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre CODE</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={centreCodePrefix}
                  readOnly
                  className="input-field flex-1 bg-gray-100 cursor-not-allowed"
                  placeholder="Block ID"
                />
                <span className="flex items-center text-gray-500">-</span>
                <input
                  type="text"
                  value={centreCodeSuffix}
                  onChange={(e) => setCentreCodeSuffix(e.target.value)}
                  className="input-field flex-1"
                  placeholder="Custom part (e.g., TMC, ARS, RRS)"
                  maxLength={10}
                />
              </div>
              {(!centreCodePrefix || !centreCodeSuffix) && (
                <p className="text-red-500 text-sm mt-1">Both parts of centre code are required</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre Name</label>
              <input
                type="text"
                {...register('name', { required: 'Centre name is required' })}
                className="input-field"
                placeholder="e.g., TNAU Main Campus"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                defaultValue="active"
                {...register('status', { required: 'Status is required' })}
                className="input-field"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const getTableHeaders = () => {
    switch (activeTab) {
      case 'state':
        return ['CODE', 'NAME', 'STATUS', 'ACTIONS'];
      case 'district':
        return ['CODE', 'NAME', 'STATE', 'STATUS', 'ACTIONS'];
      case 'block':
        return ['CODE', 'NAME', 'DISTRICT', 'STATE', 'STATUS', 'ACTIONS'];
      case 'centre':
        return ['CODE', 'NAME', 'CENTRE', 'BLOCK', 'DISTRICT', 'STATE', 'STATUS', 'ACTIONS'];
      default:
        return ['NAME', 'STATUS', 'ACTIONS'];
    }
  };

  const getTableRow = (item) => {
    const baseRow = [
      <span className="font-mono font-semibold text-blue-600">
        {activeTab === 'state' ? item.state_id : 
         activeTab === 'district' ? item.district_id : 
         activeTab === 'block' ? item.block_id : 
         activeTab === 'centre' ? item.centre_id : 
         generateCode(item, activeTab)}
      </span>,
      <span className="font-medium">{item.name}</span>
    ];

    switch (activeTab) {
      case 'state':
        return [
          ...baseRow,
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {item.status}
          </span>,
          <div className="flex space-x-2">
            <button onClick={() => openEditModal(item)} className="text-blue-600 hover:text-blue-800">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ];
      case 'district':
        return [
          ...baseRow,
          <span className="text-sm text-gray-600">{item.state_name}</span>,
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {item.status}
          </span>,
          <div className="flex space-x-2">
            <button onClick={() => openEditModal(item)} className="text-blue-600 hover:text-blue-800">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ];
      case 'block':
        return [
          ...baseRow,
          <span className="text-sm text-gray-600">{item.district_name}</span>,
          <span className="text-sm text-gray-600">{item.state_name}</span>,
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {item.status}
          </span>,
          <div className="flex space-x-2">
            <button onClick={() => openEditModal(item)} className="text-blue-600 hover:text-blue-800">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ];
      case 'centre':
        return [
          ...baseRow,
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.type === 'TNAU' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
            {item.type}
          </span>,
          <span className="text-sm text-gray-600">{item.block_name}</span>,
          <span className="text-sm text-gray-600">{item.district_name}</span>,
          <span className="text-sm text-gray-600">{item.state_name}</span>,
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {item.status}
          </span>,
          <div className="flex space-x-2">
            <button onClick={() => openEditModal(item)} className="text-blue-600 hover:text-blue-800">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Location Management</h1>
          <p className="text-gray-600">Manage states, districts, blocks, and centres</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          {activeTab !== 'centre' && (
            <button 
              onClick={() => setShowExportModal(true)}
              className="btn-secondary flex items-center"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
            </button>
          )}
          <button 
            onClick={() => {
              setEditingItem(null);
              reset();
              // Reset cascading dropdown data
              setAvailableDistricts([]);
              setAvailableBlocks([]);
              setCentreCodePrefix('');
              setCentreCodeSuffix('');
              setShowAddModal(true);
              // Set Tamil Nadu as default state for centre modal
              if (activeTab === 'centre') {
                const tamilNaduState = allStates.find(state => state.name === 'Tamil Nadu');
                if (tamilNaduState) {
                  setValue('stateCode', tamilNaduState.state_id);
                }
              }
            }}
            className="btn-primary flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {activeTab === 'state' && searchTerm !== debouncedSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            </div>
          )}
          {activeTab === 'district' && searchTerm !== debouncedSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            </div>
          )}
          {activeTab === 'block' && searchTerm !== debouncedSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            </div>
          )}
          {activeTab === 'centre' && searchTerm !== debouncedSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {getTableHeaders().map((header, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData
                .filter(item => statusFilter === 'all' || normalizeStatus(item.status) === statusFilter)
                .map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {getTableRow(item).map((cell, index) => (
                    <td key={index} className="px-6 py-4 whitespace-nowrap">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination for Districts */}
      {activeTab === 'district' && districtPagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing {((districtPagination.page - 1) * districtPagination.limit) + 1} to{' '}
            {Math.min(districtPagination.page * districtPagination.limit, districtPagination.total)} of{' '}
            {districtPagination.total} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (districtPagination.page > 1) {
                  const newPage = districtPagination.page - 1;
                  setDistrictPagination(prev => ({ ...prev, page: newPage }));
                  loadDistricts(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={districtPagination.page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {districtPagination.page} of {districtPagination.pages}
            </span>
            <button
              onClick={() => {
                if (districtPagination.page < districtPagination.pages) {
                  const newPage = districtPagination.page + 1;
                  setDistrictPagination(prev => ({ ...prev, page: newPage }));
                  loadDistricts(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={districtPagination.page >= districtPagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pagination for Blocks */}
      {activeTab === 'block' && blockPagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing {((blockPagination.page - 1) * blockPagination.limit) + 1} to{' '}
            {Math.min(blockPagination.page * blockPagination.limit, blockPagination.total)} of{' '}
            {blockPagination.total} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (blockPagination.page > 1) {
                  const newPage = blockPagination.page - 1;
                  setBlockPagination(prev => ({ ...prev, page: newPage }));
                  loadBlocks(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={blockPagination.page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {blockPagination.page} of {blockPagination.pages}
            </span>
            <button
              onClick={() => {
                if (blockPagination.page < blockPagination.pages) {
                  const newPage = blockPagination.page + 1;
                  setBlockPagination(prev => ({ ...prev, page: newPage }));
                  loadBlocks(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={blockPagination.page >= blockPagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pagination for States */}
      {activeTab === 'state' && statePagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing {((statePagination.page - 1) * statePagination.limit) + 1} to{' '}
            {Math.min(statePagination.page * statePagination.limit, statePagination.total)} of{' '}
            {statePagination.total} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (statePagination.page > 1) {
                  const newPage = statePagination.page - 1;
                  setStatePagination(prev => ({ ...prev, page: newPage }));
                  loadStates(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={statePagination.page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {statePagination.page} of {statePagination.pages}
            </span>
            <button
              onClick={() => {
                if (statePagination.page < statePagination.pages) {
                  const newPage = statePagination.page + 1;
                  setStatePagination(prev => ({ ...prev, page: newPage }));
                  loadStates(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={statePagination.page >= statePagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pagination for Centres */}
      {activeTab === 'centre' && centrePagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing {((centrePagination.page - 1) * centrePagination.limit) + 1} to{' '}
            {Math.min(centrePagination.page * centrePagination.limit, centrePagination.total)} of{' '}
            {centrePagination.total} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (centrePagination.page > 1) {
                  const newPage = centrePagination.page - 1;
                  setCentrePagination(prev => ({ ...prev, page: newPage }));
                  loadCentres(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={centrePagination.page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {centrePagination.page} of {centrePagination.pages}
            </span>
            <button
              onClick={() => {
                if (centrePagination.page < centrePagination.pages) {
                  const newPage = centrePagination.page + 1;
                  setCentrePagination(prev => ({ ...prev, page: newPage }));
                  loadCentres(newPage, debouncedSearchTerm, statusFilter);
                }
              }}
              disabled={centrePagination.page >= centrePagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingItem ? 'Edit' : 'Add'} {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h2>
              <form onSubmit={handleSubmit(editingItem ? handleEdit : handleAdd)} className="space-y-4">
                {getFormFields()}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingItem(null);
                      reset();
                      // Reset centre code state
                      setCentreCodePrefix('');
                      setCentreCodeSuffix('');
                      // Reset cascading dropdown data
                      setAvailableDistricts([]);
                      setAvailableBlocks([]);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Export {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Data
                </h2>
                <button
                  onClick={() => {
                    setShowExportModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Export Options */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-green-900">Export Options</h3>
                      <p className="text-sm text-green-700 mt-1">
                        Choose the format and data to export
                      </p>
                    </div>
                  </div>
                </div>

                {/* Export Format Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Export Format
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => exportToCSV()}
                      className="btn-secondary flex items-center justify-center p-3"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => exportToExcel()}
                      className="btn-secondary flex items-center justify-center p-3"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Export as Excel
                    </button>
                  </div>
                </div>

                {/* Data Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data to Export
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        defaultChecked
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include all fields</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        defaultChecked
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include inactive items</span>
                    </label>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Location; 